import React, { useState } from 'react';
import { XIcon, ChevronDownIcon } from 'lucide-react';
import { AVAILABLE_FILTERS, ActiveFilter } from '../types/filters';
interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFilters: ActiveFilter[];
  onApplyFilters: (filters: ActiveFilter[]) => void;
}
export function FilterModal({
  isOpen,
  onClose,
  activeFilters,
  onApplyFilters
}: FilterModalProps) {
  const [tempFilters, setTempFilters] = useState<ActiveFilter[]>(activeFilters);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set());
  if (!isOpen) return null;
  const toggleFilterExpanded = (filterId: string) => {
    setExpandedFilters(prev => {
      const next = new Set(prev);
      if (next.has(filterId)) {
        next.delete(filterId);
      } else {
        next.add(filterId);
      }
      return next;
    });
  };
  const updateFilterValue = (filterId: string, value: any, label: string) => {
    setTempFilters(prev => {
      const existing = prev.find(f => f.filterId === filterId);
      if (existing) {
        return prev.map(f => f.filterId === filterId ? {
          ...f,
          value
        } : f);
      }
      return [...prev, {
        filterId,
        value,
        label
      }];
    });
  };
  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };
  const handleReset = () => {
    setTempFilters([]);
  };
  const leadFilters = AVAILABLE_FILTERS.filter(f => f.category === 'lead');
  const callFilters = AVAILABLE_FILTERS.filter(f => f.category === 'call');
  const otherFilters = AVAILABLE_FILTERS.filter(f => f.category === 'other');
  return <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm pt-20" data-id="element-220">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-221">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-222">
          <h2 className="text-xl font-semibold text-gray-800" data-id="element-223">FILTERS</h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-224">
            <XIcon size={20} data-id="element-225" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-226">
          <div className="grid grid-cols-3 gap-8" data-id="element-227">
            {/* LEAD Section */}
            <div data-id="element-228">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-229">
                LEAD
              </h3>
              <div className="space-y-3" data-id="element-230">
                {leadFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                return <div key={filter.id} data-id="element-231">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-232">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-233">
                        <span data-id="element-234">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-235" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-236">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-237">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>

            {/* CALL Section */}
            <div data-id="element-238">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-239">
                CALL
              </h3>
              <div className="space-y-3" data-id="element-240">
                {callFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                return <div key={filter.id} data-id="element-241">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-242">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-243">
                        <span data-id="element-244">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-245" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-246">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-247">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>

            {/* OTHER Section */}
            <div data-id="element-248">
              <h3 className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wider" data-id="element-249">
                OTHER
              </h3>
              <div className="space-y-3" data-id="element-250">
                {otherFilters.map(filter => {
                const isExpanded = expandedFilters.has(filter.id);
                const currentValue = tempFilters.find(f => f.filterId === filter.id)?.value;
                if (filter.type === 'date') {
                  return <div key={filter.id} data-id="element-251">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-252">
                          {filter.label}
                        </label>
                        <input type="text" placeholder="01 Jan 2026 - 29 Jan 2026" value={currentValue || ''} onChange={e => updateFilterValue(filter.id, e.target.value, filter.label)} className="w-full px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-253" />
                      </div>;
                }
                if (filter.type === 'checkbox' && filter.options) {
                  return <div key={filter.id} data-id="element-254">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-255">
                          {filter.label}
                        </label>
                        <div className="flex space-x-3" data-id="element-256">
                          {filter.options.map(option => <label key={option} className="flex items-center cursor-pointer" data-id="element-257">
                              <input type="checkbox" className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-2 focus:ring-blue-500" data-id="element-258" />
                              <span className="ml-2 text-sm text-gray-700" data-id="element-259">
                                {option}
                              </span>
                            </label>)}
                        </div>
                      </div>;
                }
                return <div key={filter.id} data-id="element-260">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-261">
                        {filter.label}
                      </label>
                      <button onClick={() => toggleFilterExpanded(filter.id)} className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-400 transition-colors" data-id="element-262">
                        <span data-id="element-263">{currentValue || 'Select'}</span>
                        <ChevronDownIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-264" />
                      </button>
                      {isExpanded && <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1" data-id="element-265">
                          {['Option 1', 'Option 2', 'Option 3'].map(option => <button key={option} onClick={() => {
                      updateFilterValue(filter.id, option, filter.label);
                      toggleFilterExpanded(filter.id);
                    }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-white rounded transition-colors" data-id="element-266">
                                {option}
                              </button>)}
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Additional Filters Button */}
          <button className="mt-6 flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors" data-id="element-267">
            <span className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center mr-2" data-id="element-268">
              <span className="text-blue-600 text-lg leading-none" data-id="element-269">+</span>
            </span>
            ADDITIONAL FILTERS
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-270">
          <button onClick={handleReset} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium" data-id="element-271">
            Reset
          </button>
          <button onClick={handleApply} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium" data-id="element-272">
            Apply
          </button>
        </div>
      </div>
    </div>;
}